import axios from 'axios';

import api, { ApiError, handleApiResponseSafe } from '@/lib/api';
import { OperationResult } from '@/lib/operation-result';
import { tokenRefreshService } from '@/lib/auth/token-refresh';
import { LOCAL_STORAGE_KEYS } from '@/constants';

import type {
  AuthUser,
  LoginResponse,
  PlatformLoginResponse,
} from './auth';

/**
 * POST /v1/auth/login — operator login against platform-service.
 *
 * Platform's response is a BARE PlatformLoginResponse (not wrapped in OperationResult)
 * per services/platform-service/app/routers/auth.py. Body shape is
 * `{ email, password }`, not `{ identifier, password }`. We adapt both ends
 * here so the form / call site stays unchanged.
 *
 * We synthesize an `OperationResult<LoginResponse>` so the form / call site
 * stays unchanged. Platform returns the operator's id/email/firstName/lastName
 * in the body; the refresh token is delivered as an HttpOnly cookie (T10/T11)
 * and is NOT in the response body. Once platform exposes a richer /auth/me —
 * operator → role → permissions resolution — we can drop the user synthesis.
 */
export const login = async (
  identifier: string,
  password: string
): Promise<OperationResult<LoginResponse>> => {
  try {
    const response = await api.post<PlatformLoginResponse>(
      '/v1/auth/login',
      { email: identifier, password }
    );

    const { accessToken, tokenType, expiresIn, operator } = response.data;

    // Least-privileged placeholder. Platform's login body carries identity
    // only — no userType, tier or permissions — so this user grants nothing
    // and reveals no admin menu. `getCurrentUser` replaces it from /auth/me
    // immediately after, which is where real authorization comes from.
    const user: AuthUser = {
      id: operator.id,
      firstName: operator.firstName,
      lastName: operator.lastName,
      email: operator.email,
      phoneNumber: null,
      tenantId: extractTenantIdFromJwt(accessToken),
      branchId: null,
      isActive: true,
      userType: null,
      accessTier: 'self',
      roleTemplateId: null,
      role: null,
      permissions: [],
    };

    return {
      succeeded: true,
      value: {
        accessToken,
        tokenType,
        expiresIn,
        user,
      },
      errors: [],
      hasErrors: false,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        succeeded: false,
        errors: error.errors.length > 0 ? error.errors : [error.message],
        hasErrors: true,
        statusCode: error.statusCode,
      };
    }
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 503;
      const detail =
        (error.response?.data as { detail?: string } | undefined)?.detail ??
        error.message;
      return {
        succeeded: false,
        errors: [detail],
        hasErrors: true,
        statusCode: status,
      };
    }
    return {
      succeeded: false,
      errors: ['Unexpected error during login'],
      hasErrors: true,
      statusCode: 500,
    };
  }
};

export const logout = async (): Promise<OperationResult<void>> => {
  try {
    // POST /v1/auth/logout — server-side revocation (T10): clears the
    // HttpOnly refresh-token cookie and invalidates the token in the DB.
    await api.post('/v1/auth/logout');
    tokenRefreshService.removeToken();
    return {
      succeeded: true,
      errors: [],
      hasErrors: false,
    };
  } catch (error) {
    // Always clean up the local access token so a network failure still
    // signs the operator out client-side.
    tokenRefreshService.removeToken();
    return {
      succeeded: false,
      errors: [(error as Error).message],
      hasErrors: true,
    };
  }
};

// `handleApiResponseSafe`, not `handleApiResponse`: these two calls back
// public, unauthenticated forms whose whole job is to show the caller a
// failure — an expired/invalid/used token, a bad identifier. Resolving
// `{succeeded: false, ...}` lets the route's existing error toast fire
// instead of the request becoming an unhandled rejection.
export const forgotPassword = async (
  identifier: string
): Promise<OperationResult<void>> => {
  return handleApiResponseSafe(() =>
    api.post<OperationResult<void>>('/v1/auth/forgot-password', { identifier })
  );
};

export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<OperationResult<void>> => {
  return handleApiResponseSafe(() =>
    api.post<OperationResult<void>>('/v1/auth/reset-password', {
      token,
      newPassword,
    })
  );
};

/**
 * GET /v1/auth/me — operator self-identity from platform-service.
 *
 * Returns `OperationResult<AuthUser>`. The localStorage fallback below is
 * kept as belt-and-suspenders for transient outages: a real 401 still
 * clears the cache and surfaces so __root.tsx can redirect to /login;
 * other failures (network glitch, 503) revive the cached user so a brief
 * blip doesn't sign the operator out.
 */
export const getCurrentUser = async (): Promise<OperationResult<AuthUser>> => {
  const live = await handleApiResponseSafe<AuthUser>(() =>
    api.get<OperationResult<AuthUser>>('/v1/auth/me')
  );
  if (live.succeeded) {
    return live;
  }

  // Hard 401 — token actually invalid. Surface as-is; __root.tsx will
  // navigate to /login. Clear the stale cached user too.
  if (live.statusCode === 401) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
    return live;
  }

  // Anything else (404 because the endpoint doesn't exist yet, 503 because
  // platform is down, network glitch, …) — fall back to the cached user so
  // the page can render. The token interceptor will redirect to /login on a
  // real 401 from the next protected call.
  const cached = readCachedUser();
  if (cached) {
    return {
      succeeded: true,
      value: cached,
      errors: [],
      hasErrors: false,
    };
  }
  return live;
};

/**
 * Decode the `tenantId` claim out of a JWT without verifying the signature.
 * The server already validated it before issuing; we only need it for
 * client-side tenant-scope display.
 */
function extractTenantIdFromJwt(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // Base64URL → Base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = atob(padded);
    const claims = JSON.parse(decoded) as { tenantId?: unknown };
    return typeof claims.tenantId === 'string' ? claims.tenantId : null;
  } catch {
    return null;
  }
}

function readCachedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthUser>;
    if (
      typeof parsed?.id === 'string' &&
      typeof parsed?.email === 'string'
    ) {
      // Every access field falls back to its least-privileged value: a cache
      // written before these existed must not read as an admin.
      return {
        id: parsed.id,
        firstName: parsed.firstName ?? '',
        lastName: parsed.lastName ?? '',
        email: parsed.email,
        phoneNumber: parsed.phoneNumber ?? null,
        tenantId: parsed.tenantId ?? null,
        branchId: parsed.branchId ?? null,
        isActive: parsed.isActive ?? true,
        userType: parsed.userType ?? null,
        accessTier: parsed.accessTier ?? 'self',
        roleTemplateId: parsed.roleTemplateId ?? null,
        role: parsed.role ?? null,
        permissions: parsed.permissions ?? [],
      };
    }
    return null;
  } catch {
    return null;
  }
}
